import {
  deriveSpreadSeries,
  estimateLogPriceBetaResidual,
  logReturnCorrelation,
  rollingSpreadVolatility,
  rollingZScore,
  selectAnalysisMetricSeries,
  type PairedPricePoint,
} from "./series";
import {
  augmentedDickeyFuller,
  engleGrangerCointegration,
  estimateAr1HalfLife,
} from "./stationarity";
import { notAvailable, type MetricResult } from "./types";
import {
  compareISODate,
  getPairPreset,
  parseISODate,
  type DisplayMetric,
  type PairPresetId,
} from "../domain";

export const DEFAULT_WINDOW_GRID = [20, 40, 60, 90, 120, 180, 252] as const;

export interface WindowSensitivityRow {
  readonly window: number;
  readonly sampleSize: number;
  readonly localZ: number | null;
  readonly metricVolatility: number | null;
  readonly spreadVolatility: number | null;
  readonly beta: number | null;
  readonly returnCorrelation: number | null;
  readonly engleGranger: MetricResult<import("./stationarity").EngleGrangerEstimate>;
  readonly adfResidual: MetricResult<import("./stationarity").UnitRootEstimate>;
  readonly halfLife: MetricResult<import("./stationarity").HalfLifeEstimate>;
}

export interface WindowSensitivityResult {
  readonly scope: "window-sensitivity";
  readonly pairPresetId: PairPresetId;
  readonly primaryStatisticalMetric: DisplayMetric;
  readonly selectedMetric: DisplayMetric;
  readonly rows: readonly WindowSensitivityRow[];
  readonly windows: readonly number[];
  readonly overfitWarning: boolean;
  readonly stabilityDetail: string;
}

export interface WindowSensitivityOptions {
  readonly pairPresetId: PairPresetId;
  readonly metric?: DisplayMetric;
  readonly windows?: readonly number[];
}

function latestRollingValue(points: readonly { readonly value: number | null }[]): number | null {
  return points.at(-1)?.value ?? null;
}

function detectIsolatedZ(rows: readonly WindowSensitivityRow[]): boolean {
  for (let index = 1; index < rows.length - 1; index += 1) {
    const previous = rows[index - 1]?.localZ;
    const current = rows[index]?.localZ;
    const next = rows[index + 1]?.localZ;
    if (previous === null || previous === undefined || current === null || current === undefined || next === null || next === undefined) continue;
    if (Math.abs(current) >= 2 && Math.abs(previous) < 1 && Math.abs(next) < 1) return true;
  }
  return false;
}

/**
 * Points must come from one validated, unadjusted actual-contract episode.
 * Prefer passing WindowSensitivityOptions; the array overload is retained only
 * for compatibility and preserves the former raw-spread behavior.
 */
export function analyzeWindowSensitivity(
  episodePoints: readonly PairedPricePoint[],
  options: WindowSensitivityOptions,
): WindowSensitivityResult;
/** @deprecated Pass WindowSensitivityOptions so the pair preset and metric are explicit. */
export function analyzeWindowSensitivity(
  episodePoints: readonly PairedPricePoint[],
  windows?: readonly number[],
): WindowSensitivityResult;
export function analyzeWindowSensitivity(
  episodePoints: readonly PairedPricePoint[],
  optionsOrWindows: WindowSensitivityOptions | readonly number[] = DEFAULT_WINDOW_GRID,
): WindowSensitivityResult {
  const options: WindowSensitivityOptions = Array.isArray(optionsOrWindows)
    ? {
        pairPresetId: "canola-matif-rapeseed",
        metric: "raw-spread",
        windows: optionsOrWindows,
      }
    : optionsOrWindows as WindowSensitivityOptions;
  const windows = options.windows ?? DEFAULT_WINDOW_GRID;
  if (windows.length === 0 || windows.some((window) => !Number.isInteger(window) || window < 10)) {
    throw new Error("Sensitivity windows must be integer observation counts >= 10.");
  }
  if (new Set(windows).size !== windows.length) {
    throw new Error("Sensitivity windows must be unique.");
  }
  const preset = getPairPreset(options.pairPresetId);
  const selectedMetric = options.metric ?? preset.primaryStatisticalMetric;
  if (!preset.permittedMetrics.includes(selectedMetric)) {
    throw new Error(`${selectedMetric} is not permitted for ${options.pairPresetId}.`);
  }
  episodePoints.forEach((point, index) => {
    parseISODate(point.date);
    const previous = episodePoints[index - 1];
    if (previous && compareISODate(previous.date, point.date) >= 0) {
      throw new Error("Window sensitivity requires strictly date-ordered episode points.");
    }
  });
  const rows = windows.map((window): WindowSensitivityRow => {
    const sample = episodePoints.slice(-window);
    if (sample.length < window) {
      const detail = `Requested ${window}-observation window is incomplete (${sample.length} available).`;
      return {
        window,
        sampleSize: sample.length,
        localZ: null,
        metricVolatility: null,
        spreadVolatility: null,
        beta: null,
        returnCorrelation: null,
        engleGranger: notAvailable("insufficient-sample", detail, sample.length),
        adfResidual: notAvailable("insufficient-sample", detail, sample.length),
        halfLife: notAvailable("insufficient-sample", detail, sample.length),
      };
    }
    const betaMinimumSample = 20;
    const stationarityMinimumSample = 30;
    const derived = deriveSpreadSeries(sample);
    const spread = derived.map((point) => point.rawSpread);
    const beta = estimateLogPriceBetaResidual(sample, betaMinimumSample);
    const metricSeries = selectAnalysisMetricSeries(derived, beta, selectedMetric);
    const correlation = logReturnCorrelation(sample, Math.max(5, betaMinimumSample - 1));
    const eg = engleGrangerCointegration(
      sample.map((point) => point.legA),
      sample.map((point) => point.legB),
      { minimumSample: stationarityMinimumSample, lags: 1 },
    );
    const adfResidual = beta.status === "available"
      ? augmentedDickeyFuller(beta.value.residuals.map((point) => point.value), {
          minimumSample: stationarityMinimumSample,
          lags: 1,
        })
      : notAvailable(beta.reason, "Residual ADF requires an available beta residual.", sample.length);
    const stationaritySupported = eg.status === "available" &&
      (eg.value.rejectAt === "1%" || eg.value.rejectAt === "5%") &&
      adfResidual.status === "available" &&
      (adfResidual.value.rejectAt === "1%" || adfResidual.value.rejectAt === "5%");
    const halfLife = beta.status === "available"
      ? estimateAr1HalfLife(
          beta.value.residuals.map((point) => point.value),
          {
            minimumSample: stationarityMinimumSample,
            analysisWindow: window,
            stationaritySupported,
          },
        )
      : notAvailable(beta.reason, "Half-life requires an available beta residual.", sample.length);
    return {
      window,
      sampleSize: sample.length,
      localZ: metricSeries.status === "available"
        ? latestRollingValue(rollingZScore(metricSeries.value.points.map((point) => point.value), window))
        : null,
      metricVolatility: metricSeries.status === "available"
        ? latestRollingValue(rollingSpreadVolatility(
            metricSeries.value.points.map((point) => point.value),
            window,
          ))
        : null,
      spreadVolatility: latestRollingValue(rollingSpreadVolatility(spread, window)),
      beta: beta.status === "available" ? beta.value.beta : null,
      returnCorrelation: correlation.status === "available" ? correlation.value.correlation : null,
      engleGranger: eg,
      adfResidual,
      halfLife,
    };
  });
  const overfitWarning = detectIsolatedZ(rows);
  return {
    scope: "window-sensitivity",
    pairPresetId: options.pairPresetId,
    primaryStatisticalMetric: preset.primaryStatisticalMetric,
    selectedMetric,
    rows,
    windows,
    overfitWarning,
    stabilityDetail: overfitWarning
      ? "A |Z|>=2 result is isolated from both adjacent windows; treat it as window-sensitive."
      : "No isolated adjacent-window Z-score spike was detected.",
  };
}
